import { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { format, parse, isValid } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Segmented date input that mimics native date pickers but is locale-independent.
 * Format: mm / dd / yyyy with individual editable segments.
 */

const SEGMENTS = [
  { key: "mm", max: 12, min: 1, maxLen: 2, placeholder: "mm" },
  { key: "dd", max: 31, min: 1, maxLen: 2, placeholder: "dd" },
  { key: "yyyy", max: 9999, min: 1, maxLen: 4, placeholder: "yyyy" },
];

function clampSegment(value, seg) {
  const n = parseInt(value, 10);
  if (isNaN(n)) return "";
  if (n > seg.max) return String(seg.max);
  return String(n);
}

function padSegment(value, seg) {
  if (!value) return "";
  const n = parseInt(value, 10);
  if (isNaN(n) || n === 0) return "";
  return String(n).padStart(seg.maxLen, "0");
}

function buildIsoDate(parts) {
  const { dd, mm, yyyy } = parts;
  if (!dd || !mm || !yyyy) return "";
  const d = parseInt(dd, 10);
  const m = parseInt(mm, 10);
  const y = parseInt(yyyy, 10);
  if (!d || !m || !y) return "";
  if (y < 1900 || y > 2100) return "";
  const iso = `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const parsed = parse(iso, "yyyy-MM-dd", new Date());
  if (!isValid(parsed)) return "";
  return iso;
}

function partsFromIso(isoStr) {
  if (!isoStr) return { dd: "", mm: "", yyyy: "" };
  const d = parse(isoStr, "yyyy-MM-dd", new Date());
  if (!isValid(d)) return { dd: "", mm: "", yyyy: "" };
  return {
    dd: String(d.getDate()).padStart(2, "0"),
    mm: String(d.getMonth() + 1).padStart(2, "0"),
    yyyy: String(d.getFullYear()),
  };
}

function SegmentInput({ segment, value, onChange, onComplete, onBackspace, active, onFocus }) {
  const ref = useRef(null);
  const [localValue, setLocalValue] = useState(value || "");
  const bufferRef = useRef("");

  useEffect(() => {
    setLocalValue(value || "");
    bufferRef.current = value || "";
  }, [value]);

  useEffect(() => {
    if (active && ref.current) {
      ref.current.focus();
      ref.current.select();
    }
  }, [active]);

  const handleKeyDown = (e) => {
    if (e.key === "Tab") return;

    if (e.key === "ArrowRight" || e.key === "/" || e.key === "-" || e.key === ".") {
      e.preventDefault();
      onComplete();
      return;
    }
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      onBackspace();
      return;
    }
    if (e.key === "Backspace") {
      e.preventDefault();
      if (bufferRef.current) {
        const next = bufferRef.current.slice(0, -1);
        bufferRef.current = next;
        setLocalValue(next);
        onChange(next);
      } else {
        onBackspace();
      }
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      const n = parseInt(bufferRef.current || "0", 10);
      const next = Math.min(n + 1, segment.max);
      const padded = padSegment(String(next), segment);
      bufferRef.current = padded;
      setLocalValue(padded);
      onChange(padded);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const n = parseInt(bufferRef.current || "0", 10);
      const next = Math.max(n - 1, segment.min);
      const padded = padSegment(String(next), segment);
      bufferRef.current = padded;
      setLocalValue(padded);
      onChange(padded);
      return;
    }

    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
      return;
    }

    e.preventDefault();
    let next = bufferRef.current + e.key;

    const num = parseInt(next, 10);
    if (num > segment.max) {
      next = e.key;
    }

    if (next.length > segment.maxLen) {
      next = next.slice(-segment.maxLen);
    }

    bufferRef.current = next;
    setLocalValue(next);
    onChange(next);

    if (next.length >= segment.maxLen) {
      const clamped = clampSegment(next, segment);
      const padded = padSegment(clamped, segment);
      bufferRef.current = padded;
      setLocalValue(padded);
      onChange(padded);
      onComplete();
    } else if (segment.maxLen === 2 && next.length === 1) {
      const firstDigit = parseInt(next, 10);
      const maxFirstDigit = Math.floor(segment.max / 10);
      if (firstDigit > maxFirstDigit) {
        const padded = padSegment(next, segment);
        bufferRef.current = padded;
        setLocalValue(padded);
        onChange(padded);
        onComplete();
      }
    }
  };

  const handleFocus = () => {
    onFocus();
    if (ref.current) {
      setTimeout(() => ref.current?.select(), 0);
    }
  };

  const width = segment.maxLen === 4 ? "w-[36px]" : "w-[26px]";

  return (
    <input
      ref={ref}
      type="text"
      inputMode="numeric"
      value={localValue}
      placeholder={segment.placeholder}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onChange={() => {}}
      className={cn(
        "bg-transparent text-center text-sm outline-none placeholder:text-muted-foreground selection:bg-indigo-100",
        width,
      )}
      aria-label={segment.placeholder}
    />
  );
}

SegmentInput.propTypes = {
  segment: PropTypes.object.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onBackspace: PropTypes.func.isRequired,
  active: PropTypes.bool,
  onFocus: PropTypes.func.isRequired,
};

/**
 * Locale-independent DatePicker with segmented input (mm / dd / yyyy).
 *
 * Props:
 *  - value: ISO date string (yyyy-MM-dd) or empty string
 *  - onChange: receives ISO date string (yyyy-MM-dd) or empty string
 *  - className: additional classes for the wrapper
 *  - inputClassName: additional classes for the input container
 *  - id / testId: for labeling and testing
 */
export function DatePicker({
  value,
  onChange,
  className,
  inputClassName,
  id,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [parts, setParts] = useState(() => partsFromIso(value));
  const [activeSegment, setActiveSegment] = useState(-1);
  const containerRef = useRef(null);

  useEffect(() => {
    setParts(partsFromIso(value));
  }, [value]);

  const emitChange = useCallback((newParts) => {
    const iso = buildIsoDate(newParts);
    if (iso && iso !== value) {
      onChange(iso);
    } else if (!newParts.dd && !newParts.mm && !newParts.yyyy && value) {
      onChange("");
    }
  }, [onChange, value]);

  const handleSegmentChange = (key, segValue) => {
    const newParts = { ...parts, [key]: segValue };
    setParts(newParts);
    emitChange(newParts);
  };

  const handleSegmentComplete = (index) => {
    if (index < SEGMENTS.length - 1) {
      setActiveSegment(index + 1);
    }
  };

  const handleSegmentBackspace = (index) => {
    if (index > 0) {
      setActiveSegment(index - 1);
    }
  };

  const handleCalendarSelect = (date) => {
    if (date && isValid(date)) {
      const iso = format(date, "yyyy-MM-dd");
      onChange(iso);
      setParts(partsFromIso(iso));
    } else {
      onChange("");
      setParts({ dd: "", mm: "", yyyy: "" });
    }
    setOpen(false);
  };

  const handleContainerClick = (e) => {
    // Don't steal focus from already-focused segments or the calendar button
    if (e.target.tagName === "INPUT" || e.target.tagName === "BUTTON") return;
    if (activeSegment < 0) {
      setActiveSegment(0);
    }
  };

  const selectedDate = value
    ? (() => { const d = parse(value, "yyyy-MM-dd", new Date()); return isValid(d) ? d : undefined; })()
    : undefined;

  return (
    <div className={cn("relative", className)} id={id} data-testid={testId}>
      <Popover open={open} onOpenChange={setOpen}>
        <div
          ref={containerRef}
          onClick={handleContainerClick}
          className={cn(
            "flex h-11 sm:h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-1 shadow-sm transition-colors cursor-text",
            "focus-within:ring-1 focus-within:ring-ring",
            inputClassName,
          )}
        >
          <div className="flex items-center flex-1 min-w-0">
            {SEGMENTS.map((seg, i) => (
              <div key={seg.key} className="flex items-center">
                {i > 0 && (
                  <span className="text-muted-foreground mx-1 text-sm select-none">/</span>
                )}
                <SegmentInput
                  segment={seg}
                  value={parts[seg.key]}
                  onChange={(v) => handleSegmentChange(seg.key, v)}
                  onComplete={() => handleSegmentComplete(i)}
                  onBackspace={() => handleSegmentBackspace(i)}
                  active={activeSegment === i}
                  onFocus={() => setActiveSegment(i)}
                />
              </div>
            ))}
          </div>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="ml-auto flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors shrink-0"
              aria-label="Open calendar"
            >
              <CalendarIcon size={16} />
            </button>
          </PopoverTrigger>
        </div>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleCalendarSelect}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

DatePicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  id: PropTypes.string,
  testId: PropTypes.string,
};
